// Role-name normalization helpers.
//
// Ensures that all role comparisons are made using the standardized,
// lowercase database identifiers.

export const ROLE = Object.freeze({
  SUPER_ADMIN: "superadmin",
  ADMIN: "admin",
  CENTER_ADMIN: "center_admin",
  STARTUP_FOUNDER: "startup_founder",
  MENTOR: "mentor",
  JURY_MEMBER: "jury_member",
});

const ADMIN_LIKE = new Set([
  ROLE.SUPER_ADMIN,
  ROLE.ADMIN,
  ROLE.CENTER_ADMIN,
]);

// Roles that can manage center-scoped resources (programs, funding, mentors,
// jury, reports). Mirrors the backend `CAN_MANAGE` set in utils/roles.py so
// FE permission gates match BE enforcement exactly.
const CAN_MANAGE = new Set([
  ROLE.SUPER_ADMIN,
  ROLE.ADMIN,
  ROLE.CENTER_ADMIN,
]);

export function normalizeRole(roleName) {
  if (!roleName) return "";
  return String(roleName).toLowerCase().trim();
}

export function isSuperAdmin(roleName) {
  return normalizeRole(roleName) === ROLE.SUPER_ADMIN;
}

export function isCenterAdmin(roleName) {
  return normalizeRole(roleName) === ROLE.CENTER_ADMIN;
}

export function isStartupFounder(roleName) {
  return normalizeRole(roleName) === ROLE.STARTUP_FOUNDER;
}

export function isMentor(roleName) {
  return normalizeRole(roleName) === ROLE.MENTOR;
}

export function isJuryMember(roleName) {
  return normalizeRole(roleName) === ROLE.JURY_MEMBER;
}

export function isAdminLike(roleName) {
  return ADMIN_LIKE.has(normalizeRole(roleName));
}

// True for any role that can manage center-scoped resources (create/edit programs,
// approve funding, manage mentors/jury, etc.). Matches BE `can_manage`.
export function canManage(roleName) {
  return CAN_MANAGE.has(normalizeRole(roleName));
}

// Returns true if `roleName` matches any of the `allowed` strings after
// normalizing both sides — use this instead of `allowed.includes(role)`.
export function roleMatchesAny(roleName, allowed) {
  if (!Array.isArray(allowed) || allowed.length === 0) return false;
  const normalized = normalizeRole(roleName);
  return allowed.some((r) => normalizeRole(r) === normalized);
}

// Global platform roles keep fixed sidebar layouts. Every other role
// (startup_founder, mentor, jury_member, custom) is permission-driven.
const GLOBAL_PLATFORM_ROLES = new Set([
  ROLE.SUPER_ADMIN,
  ROLE.CENTER_ADMIN,
  "master",
]);

export function isGlobalPlatformRole(roleName) {
  return GLOBAL_PLATFORM_ROLES.has(normalizeRole(roleName));
}
